import DOMPurify from 'dompurify';

/**
 * Sanitizes HTML content to prevent XSS attacks
 * @param {string} html - The HTML string to sanitize
 * @returns {string} - Sanitized HTML string
 */
export const sanitizeHTML = (html) => {
  if (!html || typeof html !== 'string') {
    return '';
  }

  // Configure DOMPurify to allow safe HTML formatting
  const config = {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 's', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'a', 'img',
      'div', 'span', 'hr'
    ],
    ALLOWED_ATTR: [
      'href', 'title', 'alt', 'src', 'class', 'style'
    ],
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    KEEP_CONTENT: true,
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
    RETURN_TRUSTED_TYPE: false
  };

  return DOMPurify.sanitize(html, config);
};

/**
 * Sanitizes HTML for display (stricter than for storage)
 * Handles both HTML and plain text content for backward compatibility
 * @param {string} html - The HTML string to sanitize
 * @returns {string} - Sanitized HTML string safe for rendering
 */
export const sanitizeHTMLForDisplay = (html) => {
  if (!html || typeof html !== 'string') {
    return '';
  }

  // Check if content contains HTML tags
  const hasHTML = /<[^>]+>/.test(html);
  
  if (hasHTML) {
    // Content has HTML, sanitize it
    return sanitizeHTML(html);
  } else {
    // Plain text content - convert line breaks to <br> and escape HTML entities
    return html
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/\n/g, '<br>');
  }
};

/**
 * Strips HTML tags from content for plain text previews
 * @param {string} html - The HTML string to strip
 * @returns {string} - Plain text without HTML tags
 */
export const stripHTML = (html) => {
  if (!html || typeof html !== 'string') {
    return '';
  }
  
  // Use DOMPurify to get text content, or fallback to regex if DOMPurify not available
  try {
    if (typeof document !== 'undefined') {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || '';
    }
  } catch (e) {
    // Fallback to regex if DOM is not available
  }
  
  // Fallback: simple regex to remove HTML tags
  return html
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
    .replace(/&amp;/g, '&') // Replace &amp; with &
    .replace(/&lt;/g, '<') // Replace &lt; with <
    .replace(/&gt;/g, '>') // Replace &gt; with >
    .replace(/&quot;/g, '"') // Replace &quot; with "
    .replace(/&#39;/g, "'") // Replace &#39; with '
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * Plain-text excerpt for list cards (rich editor HTML → readable preview).
 * @param {string} html - Post/body HTML
 * @param {number} maxLength - Max characters before ellipsis
 */
export const getPlainTextPreview = (html, maxLength = 200) => {
  const plain = stripHTML(html);
  if (!plain) return '';
  if (!maxLength || plain.length <= maxLength) return plain;
  const slice = plain.slice(0, maxLength).trim();
  const lastSpace = slice.lastIndexOf(' ');
  const trimmed = lastSpace > maxLength * 0.6 ? slice.slice(0, lastSpace) : slice;
  return `${trimmed}…`;
};

